/**
 * The credit reads, as query options, and what every booking write reads again.
 *
 * Every key sits under the booking root (`BOOKING_ROOT`), so a write anywhere on `/rezerwacje`
 * — or an act on the client card — reads the summary and the ledgers again with everything else
 * (`readBookingAgain`).
 */

import { infiniteQueryOptions, queryOptions, type QueryClient } from '@tanstack/react-query';

import { customerScheduleRoot } from '~/entities/calendar-event';
import { getBookingPolicy } from '~/shared/api/generated/endpoints/booking-policy/booking-policy';
import {
  getCreditLedgerCustomerId,
  getCreditLedgerSummary,
} from '~/shared/api/generated/endpoints/credit-ledger/credit-ledger';

/** The root every booking read sits under — the calendar's, the hours', the policy's, the credits'. */
export const BOOKING_ROOT = ['booking'] as const;

const ROOT = BOOKING_ROOT;

/** A minute: a read just seen answers from the cache rather than drawing bars again. */
const STALE_MS = 60_000;

/**
 * What every write reads again: every read on the screen (a session changes any of them), and the
 * client's own schedule — their card's calendar and the tasks built on it — which shows the same
 * session from the other side.
 */
export function readBookingAgain(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ROOT }),
    queryClient.invalidateQueries({ queryKey: customerScheduleRoot }),
  ]);
}

/**
 * The trainer's credit standing with every client (`GET /credit-ledger/summary`, trainer-only) —
 * whether credits run at all (R18) and each client's balance and sessions owed. Under the booking
 * root, so every write on this screen reads it again.
 *
 * A failed summary is NOT read again because another surface mounted over it: the panel said it
 * failed and offers the retry, and a read the trainer did not ask for would land mid-view and grow
 * the panel by its credit lines.
 */
export function creditSummaryQueryOptions() {
  return queryOptions({
    queryKey: [...ROOT, 'credits', 'summary'] as const,
    queryFn: ({ signal }) => getCreditLedgerSummary(undefined, signal),
    staleTime: STALE_MS,
    retryOnMount: false,
  });
}

/**
 * One client's ledger, first page (`GET /credit-ledger/{customerId}`) — read only to learn whether a
 * session's charge was already forgiven (R14). Best-effort in the safe direction: a forgiveness older
 * than the first page is not found, and naming the session again changes nothing on the server.
 */
export function customerLedgerQueryOptions(customerId: number, enabled: boolean) {
  return queryOptions({
    queryKey: [...ROOT, 'credits', 'ledger', customerId] as const,
    queryFn: ({ signal }) => getCreditLedgerCustomerId(customerId, undefined, undefined, signal),
    staleTime: STALE_MS,
    enabled,
  });
}

/**
 * One client's whole ledger, page by page (`GET /credit-ledger/{customerId}`, keyset: the answer's
 * `nextCursor` is handed back verbatim) — the Rozliczenia tab's history. Its own key beside the
 * session panel's first-page read: the two ask different questions of the same rows, and both sit
 * under the booking root, so every write reads both again.
 */
export function customerLedgerPagesQueryOptions(customerId: number) {
  return infiniteQueryOptions({
    queryKey: [...ROOT, 'credits', 'ledger', customerId, 'pages'] as const,
    queryFn: ({ pageParam, signal }) =>
      getCreditLedgerCustomerId(
        customerId,
        pageParam === undefined ? undefined : { cursor: pageParam },
        undefined,
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
    staleTime: STALE_MS,
    // No client yet — the card stands in its waiting shape while the list is read.
    enabled: customerId > 0,
  });
}

/**
 * The trainer's booking policy — whether credits and self-booking run, and the settings under them.
 * Read by the booking screen's tabs and by the client card's settings dialog („teraz: …").
 */
export function bookingPolicyQueryOptions() {
  return queryOptions({
    queryKey: [...ROOT, 'policy'] as const,
    queryFn: ({ signal }) => getBookingPolicy(undefined, signal),
    staleTime: STALE_MS,
  });
}

/**
 * How long a route waits for the policy before drawing anyway — under the router's 300ms pending
 * delay, so a slow or failing policy never shows the full-screen loader in place of the screen.
 */
const POLICY_PRELOAD_MS = 250;

/**
 * The booking policy, read BEFORE a screen whose layout depends on whether credits run — the
 * client card's „Rozliczenie" band and the roster's column — so its first frame holds exactly the
 * room the answer needs. For a route's loader.
 *
 * It never holds the route: a cached policy answers at once, a live one gets `POLICY_PRELOAD_MS`,
 * and a slow or failed one is left to the screen's own read (no retry here — the screen's read
 * retries). In that case the screen decides on the credit summary, as it would with no policy.
 */
export function preloadBookingPolicy(queryClient: QueryClient): Promise<void> {
  const read = queryClient.ensureQueryData({ ...bookingPolicyQueryOptions(), retry: false }).then(
    () => undefined,
    () => undefined,
  );
  const bound = new Promise<void>((resolve) => {
    setTimeout(resolve, POLICY_PRELOAD_MS);
  });
  return Promise.race([read, bound]);
}
